import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import logger from '../utils/logger';

const DeliveryLocationContext = createContext();

export const useDeliveryLocation = () => {
  const context = useContext(DeliveryLocationContext);
  if (!context) {
    throw new Error('useDeliveryLocation must be used within a DeliveryLocationProvider');
  }
  return context;
};

export const DeliveryLocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchLocations = async () => {
    try {
      setLoading(true);
      // Use /all endpoint only for admins, otherwise use public endpoint
      const endpoint = user?.role === 'admin' ? '/delivery-locations/all' : '/delivery-locations';
      const response = await api.get(endpoint);
      
      // Handle both response formats (with and without .data wrapper)
      if (response.success || response.data?.success) {
        setLocations(response.data?.data || response.data || []);
      }
    } catch (error) {
      // Silently fail for non-admin users trying to access /all
      if (error.response?.status === 403) {
        logger.debug('Admin access required for /all endpoint, using public endpoint instead');
        // Fallback to public endpoint
        try {
          const publicResponse = await api.get('/delivery-locations');
          if (publicResponse.success || publicResponse.data?.success) {
            setLocations(publicResponse.data?.data || publicResponse.data || []);
          }
        } catch (fallbackError) {
          logger.error('Error fetching delivery locations (fallback):', fallbackError);
        }
      } else {
        logger.error('Error fetching delivery locations:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const addLocation = async (locationData) => {
    try {
      const response = await api.post('/delivery-locations', locationData);
      if (response.data.success) {
        await fetchLocations();
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to add location');
    } catch (error) {
      logger.error('Error adding delivery location:', error);
      throw error;
    }
  };

  const updateLocation = async (id, locationData) => {
    try {
      const response = await api.put(`/delivery-locations/${id}`, locationData);
      if (response.data.success) {
        await fetchLocations();
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update location');
    } catch (error) {
      logger.error('Error updating delivery location:', error);
      throw error;
    }
  };

  const deleteLocation = async (id) => {
    try {
      const response = await api.delete(`/delivery-locations/${id}`);
      if (response.data.success) {
        await fetchLocations();
        return true;
      }
      throw new Error(response.data.message || 'Failed to delete location');
    } catch (error) {
      logger.error('Error deleting delivery location:', error);
      throw error;
    }
  };

  const value = {
    locations,
    loading,
    fetchLocations,
    addLocation,
    updateLocation,
    deleteLocation
  };

  return (
    <DeliveryLocationContext.Provider value={value}>
      {children}
    </DeliveryLocationContext.Provider>
  );
};

